import { Avatar, Divider, Grid, Tooltip, Typography } from '@mui/material';
import Astrict from '../../GlobalComponent/Astrict/Astrict';
import GlobalTextfield from '../../GlobalComponent/GlobalTextfield/GlobalTextfield';
import globalStyles from "../../Styles/ComponentStyles/global.module.scss";
import { useContext, useRef, useState } from 'react';
import { UserContext, UserContextPayload } from '../../Context/UserDetailContext';
import { useSnackbar } from '../../GlobalComponent/GlobalSnackbar/GlobalSnackbar';
import agent from '../../service/api';
import GlobalButton from '../../Utils/GlobalUtilsComponents/GlobalButton';
import { useNavigate } from 'react-router-dom';
import LockIcon from '@mui/icons-material/Lock';
import EastIcon from '@mui/icons-material/East';
import GlobalPopUp from '../../GlobalComponent/GlobalPopup/GlobalPopup';
import ChangePassword from '../ChangePassword/ChangePassword';

const REQUIRED_FIELD_MESSAGE = 'This field is required';

const MyAccount = () => {
    const { UserMe, setFlagLoader, userDetail, setUserDetail } = useContext(UserContext) as UserContextPayload;
    const { showSnackbar } = useSnackbar();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const navigate = useNavigate();

    const [flagChangePassword, setFlagChangePassword] = useState(false);

    const [formState, setFormState] = useState<any>({
        firstname: userDetail?.user_data?.firstname || "",
        lastname: userDetail?.user_data?.lastname || "",
        user_contact: userDetail?.user_data?.user_contact || "",
        user_profile_image: userDetail?.user_data?.user_profile_image || "",
        user_profile_image_id: userDetail?.user_data?.user_profile_image_id || "",
    });

    const [errors, setErrors] = useState({
        firstname: "",
        lastname: "",
        user_contact: ""
    });

    const userDetails = async () => {
        try {
            setFlagLoader(true);
            const response = await UserMe();
            if (response?.status) {
                setFlagLoader(false);
                const userData = response?.data?.[0];
                if (userData) {
                    setUserDetail({
                        ...userData,
                        flagLoggedIn: true
                    });
                    setFormState({
                        firstname: userData?.user_data?.firstname || "",
                        lastname: userData?.user_data?.lastname || "",
                        user_contact: userData?.user_data?.user_contact || "",
                        user_profile_image: userData?.user_data?.user_profile_image || "",
                        user_profile_image_id: userData?.user_data?.user_profile_image_id || "",
                    });
                }
            } else {
                setFlagLoader(false);
                showSnackbar(response?.error?.message || "Something went wrong!", "error");
            }
        } catch (error) {
            setFlagLoader(false);
            console.error(error, "Error fetching data!");
        }
    };

    const handleProfilePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const payload = new FormData();
        payload.append('file', file);
        payload.append('profile_flag', "true");

        try {
            setFlagLoader(true);

            const response = await agent.uploadResourceAPI.fileUploadPost(payload);
            if (response?.status !== 401 && response?.status !== 400) {

                const file_id_to_send = response.data?.thumbnail_file_id;

                const uploadResponse: any = await agent.getUploadResourceDataAPI.get(file_id_to_send);
                if (uploadResponse?.status !== 401 && uploadResponse?.status !== 400) {
                    const newImageUrl = uploadResponse?.data?.file_url;
                    const newImageId = file_id_to_send;

                    setFormState((prev: any) => ({
                        ...prev,
                        user_profile_image: newImageUrl,
                        user_profile_image_id: newImageId
                    }));

                    const updatePayload = {
                        firstname: formState.firstname || userDetail?.user_data?.firstname,
                        lastname: formState.lastname || userDetail?.user_data?.lastname,
                        contact: formState.user_contact || userDetail?.user_data?.user_contact,
                        image_flag: true,
                        image_id: newImageId,
                    };

                    const updateResponse: any = await agent.updateAgentData.put(updatePayload);
                    if (updateResponse?.status !== 401 && updateResponse?.status !== 400) {
                        showSnackbar("Profile photo updated successfully!", "success");
                        await userDetails();
                    } else {
                        showSnackbar(updateResponse?.error?.message || "Failed to save profile photo.", "error");
                    }
                }

            } else {
                showSnackbar(response?.error?.message || "Failed to upload profile photo.", "error");
            }
        } catch (error) {
            console.error(error, "Error uploading profile photo!");
            showSnackbar("Error uploading profile photo!", "error");
        } finally {
            setFlagLoader(false);
        }
    };

    const validateAllFields = () => {
        const { firstname, lastname, user_contact } = formState;
        const nextErrors = {
            firstname: firstname?.trim() ? '' : REQUIRED_FIELD_MESSAGE,
            lastname: lastname?.trim() ? '' : REQUIRED_FIELD_MESSAGE,
            user_contact: user_contact?.trim() ? '' : REQUIRED_FIELD_MESSAGE,
        };
        setErrors(nextErrors);
        return !nextErrors.firstname && !nextErrors.lastname && !nextErrors.user_contact;
    };

    const handleUpdateAccountDetails = async () => {
        const { firstname, lastname, user_contact, user_profile_image_id } = formState;

        if (!validateAllFields()) {
            showSnackbar("Please fill all required fields!", "error");
            return;
        }

        try {
            setFlagLoader(true);

            const payload = {
                firstname: firstname || userDetail?.user_data?.firstname,
                lastname: lastname || userDetail?.user_data?.lastname,
                contact: user_contact || userDetail?.user_data?.user_contact,
                image_flag: !!formState.user_profile_image,
                image_id: user_profile_image_id || userDetail?.user_data?.user_profile_image_id,
            };

            const response = await agent.updateAgentData.put(payload);

            if (response?.status !== 401 && response?.status !== 400) {
                showSnackbar("Account Details Updated Successfully!", "success");
                await userDetails(); // Refresh the user details from the API
            }
        } catch (error) {
            console.error(error, "Error updating account details");
            showSnackbar("Failed to update account details.", "error");
        } finally {
            setFlagLoader(false);
        }
    };

    const validateField = (name: keyof typeof errors, value: string) => {
        let error = '';
        if (!value?.trim()) {
            error = REQUIRED_FIELD_MESSAGE;
        }
        setErrors((prev) => ({ ...prev, [name]: error }));
    };

    const handleChange = (field: keyof typeof errors, value: string) => {
        setFormState((prev: any) => ({
            ...prev,
            [field]: value
        }));
        validateField(field, value);
    };

    return (
        <>
            {
                flagChangePassword &&
                <GlobalPopUp
                    open={flagChangePassword}
                    onClose={() => { setFlagChangePassword(false) }}
                    title={"Change Password"}
                    showCloseIcon={true}
                    flagDivider
                    maxWidth={"sm"}
                >
                    <ChangePassword onCancel={() => setFlagChangePassword(false)} />
                </GlobalPopUp>
            }
            <Grid item xs={12} className={globalStyles.myAccountPageWrapper}>
                <Grid container spacing={2.5}>
                    <Grid item xs={12}>
                        <Grid container className={globalStyles.myAccountMainWrapper}>
                            <Grid item xs={12}>
                                <Grid container gap={"24px"}>
                                    <Grid item xs={12} sm={2} md={2} lg={1} xl={1} className={globalStyles.myAccountAvatarItem}>
                                        <Tooltip title="Upload Image" arrow>
                                            <Avatar
                                                className={globalStyles.myAccountContainer}
                                                src={formState?.user_profile_image || userDetail?.user_data?.user_profile_image}
                                                onClick={() => fileInputRef.current?.click()}
                                            />
                                        </Tooltip>
                                    </Grid>
                                    <Grid item xs={12} sm={10} md={9} lg={9} xl={9}>
                                        <Grid container className={globalStyles.myAccountProfileContent}>
                                            <Grid item xs={12}>
                                                <Typography component="h3" className={globalStyles.myAccountCardTitle}>
                                                    Profile Photo
                                                </Typography>
                                                <Typography component="p" className={globalStyles.myAccountCardSubtext}>
                                                    Update your profile picture to personalize your account
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <input
                                                    ref={fileInputRef}
                                                    className={globalStyles.hiddenFileInput}
                                                    id="profileUpload"
                                                    name="upload thumbnail"
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={handleProfilePhotoUpload}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Grid container className={globalStyles.myAccountActionsWrapper}>
                                                    <Grid item>
                                                        <GlobalButton
                                                            value="Upload Photo"
                                                            className={globalStyles.myAccUploadPhotoBtn}
                                                            onClick={() => fileInputRef.current?.click()}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Divider
                                sx={{
                                    opacity: 0.8,
                                    borderColor: "#C2C2C2",
                                    height: "1px",
                                    width: "100%",
                                    margin: "0 auto"
                                }}
                            />
                            <Grid item xs={12}>
                                <Grid container gap={"24px"}>
                                    <Grid item xs={12}>
                                        <Typography component="h3" className={globalStyles.myAccountCardTitle}>
                                            Personal Information
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4} className={globalStyles.myAccountFormGrid}>
                                        <Grid container>
                                            <Grid item xs={12} className={globalStyles.globalPFLabel}>
                                                <Typography component={'span'} className={globalStyles.globalPFTextfieldLabel}>
                                                    First Name <Astrict />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <GlobalTextfield
                                                    variant="outlined"
                                                    placeholder="Enter First Name"
                                                    size="medium"
                                                    fullWidth
                                                    autoComplete="off"
                                                    stylingClassName={globalStyles.globalPFTextfield}
                                                    value={formState.firstname}
                                                    onChange={(e) => handleChange('firstname', e.target.value)}
                                                    error={Boolean(errors.firstname)}
                                                    helperText={errors.firstname || undefined}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4} className={globalStyles.myAccountFormGrid}>
                                        <Grid container>
                                            <Grid item xs={12} className={globalStyles.globalPFLabel}>
                                                <Typography component={'span'} className={globalStyles.globalPFTextfieldLabel}>
                                                    Last Name <Astrict />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <GlobalTextfield
                                                    variant="outlined"
                                                    placeholder="Enter Last Name"
                                                    size="medium"
                                                    fullWidth
                                                    autoComplete="off"
                                                    stylingClassName={globalStyles.globalPFTextfield}
                                                    value={formState.lastname}
                                                    onChange={(e) => handleChange('lastname', e.target.value)}
                                                    error={Boolean(errors.lastname)}
                                                    helperText={errors.lastname || undefined}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4} className={globalStyles.myAccountFormGrid}>
                                        <Grid container>
                                            <Grid item xs={12} className={globalStyles.globalPFLabel}>
                                                <Typography component={'span'} className={globalStyles.globalPFTextfieldLabel}>
                                                    Contact <Astrict />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <GlobalTextfield
                                                    variant="outlined"
                                                    placeholder="Enter Contact"
                                                    size="medium"
                                                    fullWidth
                                                    autoComplete="off"
                                                    stylingClassName={globalStyles.globalPFTextfield}
                                                    value={formState.user_contact}
                                                    onChange={(e) => handleChange('user_contact', e.target.value)}
                                                    error={Boolean(errors.user_contact)}
                                                    helperText={errors.user_contact || undefined}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4} className={globalStyles.myAccountFormGrid}>
                                        <Grid container>
                                            <Grid item xs={12} className={globalStyles.globalPFLabel}>
                                                <Typography component={'span'} className={globalStyles.globalPFTextfieldLabel}>
                                                    Email <Astrict />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <GlobalTextfield
                                                    variant="outlined"
                                                    placeholder="Enter Contact"
                                                    size="medium"
                                                    fullWidth
                                                    autoComplete="off"
                                                    stylingClassName={globalStyles.globalPFTextfield}
                                                    value={userDetail?.user_data?.user_email || ""}
                                                    disabled
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <GlobalButton
                                            value="Update profile"
                                            className={globalStyles.myAccUpdateProfileBtn}
                                            onClick={() => { handleUpdateAccountDetails() }}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item xs={12}>
                        <Grid container className={globalStyles.myAccountMainWrapper} alignItems={"start"}>
                            <Grid item>
                                <div className={globalStyles.myAccountChangePwdIconWrapper}>
                                    <LockIcon />
                                </div>
                            </Grid>
                            <Grid item>
                                <Grid container gap={1}>
                                    <Grid item xs={12}>
                                        <Typography component="h3" className={globalStyles.myAccountCardTitle}>
                                            Change Password
                                        </Typography>
                                        <Typography component="p" className={globalStyles.myAccountCardSubtext}>
                                            Update your password to keep your account secure
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography
                                            component="span"
                                            className={globalStyles.myAccountChangePwdLink}
                                            onClick={() => setFlagChangePassword(true)}
                                        >
                                            Change Password <EastIcon sx={{ fontSize: 20 }} />
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
};

export default MyAccount;